import { createSeededRandom, hashSeed, type SeededRandom } from "../world/seed";
import { CLASSIC_LIFT } from "../world/classicMountain";
import {
  LIFT_ROUTE,
  liftDistanceOnLane,
  liftRoutePoint,
  wrapLiftDistance,
} from "../world/liftRoute";

export const LIVING_SLOPE_TUNING = Object.freeze({
  actorCullDistanceMeters: 150,
  beginnerFallIntervalSeconds: 3.2,
  chairDropChancePerFixedStep: 1 / 4_096,
  chairLoopBottomY: CLASSIC_LIFT.towers.at(-1)!.y,
  chairLoopLengthMeters: LIFT_ROUTE.length,
  chairLoopTopY: CLASSIC_LIFT.towers[0]!.y,
  chairSpeedMetersPerSecond: 2,
  dogContactRadiusMeters: 1.35,
  maximumActors: 14,
  snowboarderFootprintRadiusMeters: 0.75,
  snowboarderEntryDistanceMeters: 46,
  walkingTreeWakeRadiusMeters: 14,
});

export type LivingActorKind =
  "beginner" | "chairlift" | "dog" | "snowboarder" | "walking-tree";

export type LivingActorState =
  | "carve-left"
  | "carve-right"
  | "cruising"
  | "dropping-skier"
  | "dropping-snowboarder"
  | "empty"
  | "entering"
  | "fallen-backward"
  | "fallen-forward"
  | "idle"
  | "jumping"
  | "knocked"
  | "occupied"
  | "recovering"
  | "running-left"
  | "running-right"
  | "tumbling-1"
  | "tumbling-2"
  | "tumbling-3"
  | "tumbling-4"
  | "walking-left"
  | "walking-right"
  | "wobble";

export type LivingActorEventType =
  | "beginner-fell"
  | "beginner-recovered"
  | "chair-looped"
  | "chair-rider-dropped"
  | "dog-hit"
  | "dog-recovered"
  | "snowboarder-entered"
  | "snowboarder-jumped"
  | "snowboarder-landed"
  | "snowboarder-tumbled"
  | "walking-tree-moved"
  | "walking-tree-woke";

export interface LivingActorSnapshot {
  readonly active: boolean;
  readonly direction: -1 | 0 | 1;
  readonly id: string;
  readonly kind: LivingActorKind;
  readonly riderCount: 0 | 1 | 2;
  readonly source: "chairlift" | "mountain" | "top-entry";
  readonly state: LivingActorState;
  readonly stateSeconds: number;
  readonly velocityX: number;
  readonly velocityY: number;
  readonly worldX: number;
  readonly worldY: number;
}

export interface LivingActorEvent {
  readonly actorId: string;
  readonly atSeconds: number;
  readonly id: string;
  readonly terrainObjectId: string | null;
  readonly type: LivingActorEventType;
  readonly worldX: number;
  readonly worldY: number;
}

export interface LivingSlopeSnapshot {
  readonly actorCount: number;
  readonly actors: readonly LivingActorSnapshot[];
  readonly bounded: boolean;
  readonly lastEvent: LivingActorEventType | "none";
  readonly simulationSeconds: number;
  readonly workUnitsLastStep: number;
}

export interface LivingSlopePlayer {
  readonly simulationSeconds: number;
  readonly viewportBottomMeters?: number;
  readonly viewportHalfWidthMeters?: number;
  readonly viewportTopMeters?: number;
  readonly worldX: number;
  readonly worldY: number;
}

export interface LivingSlopeRamp {
  readonly footprintRadius: number;
  readonly id: string;
  readonly worldX: number;
  readonly worldY: number;
}

interface MutableActor {
  liftDistance: number;
  active: boolean;
  direction: -1 | 0 | 1;
  id: string;
  kind: LivingActorKind;
  lastRampId: string | null;
  riderCount: 0 | 1 | 2;
  source: "chairlift" | "mountain" | "top-entry";
  state: LivingActorState;
  stateSeconds: number;
  velocityX: number;
  velocityY: number;
  worldX: number;
  worldY: number;
}

function actor(
  value: Omit<
    MutableActor,
    "lastRampId" | "stateSeconds" | "velocityX" | "velocityY" | "liftDistance"
  > &
    Partial<Pick<MutableActor, "stateSeconds" | "velocityX" | "velocityY">>,
): MutableActor {
  return {
    liftDistance:
      value.kind === "chairlift"
        ? liftDistanceOnLane(value.worldY, value.direction)
        : 0,
    lastRampId: null,
    stateSeconds: 0,
    velocityX: 0,
    velocityY: 0,
    ...value,
  };
}

function distanceToSegment(
  pointX: number,
  pointY: number,
  startX: number,
  startY: number,
  endX: number,
  endY: number,
): number {
  const segmentX = endX - startX;
  const segmentY = endY - startY;
  const lengthSquared = segmentX * segmentX + segmentY * segmentY;
  const ratio =
    lengthSquared === 0
      ? 0
      : Math.max(
          0,
          Math.min(
            1,
            ((pointX - startX) * segmentX + (pointY - startY) * segmentY) /
              lengthSquared,
          ),
        );
  return Math.hypot(
    pointX - (startX + segmentX * ratio),
    pointY - (startY + segmentY * ratio),
  );
}

const ACTOR_SPAWN_MARGIN_METERS = 5;

function viewportHalfWidth(player: Readonly<LivingSlopePlayer>): number {
  return player.viewportHalfWidthMeters ?? 55;
}

function viewportTop(player: Readonly<LivingSlopePlayer>): number {
  return player.viewportTopMeters ?? 45;
}

function viewportBottom(player: Readonly<LivingSlopePlayer>): number {
  return player.viewportBottomMeters ?? 45;
}

function topSpawnY(player: Readonly<LivingSlopePlayer>): number {
  return player.worldY - viewportTop(player) - ACTOR_SPAWN_MARGIN_METERS;
}

function sideSpawnX(
  player: Readonly<LivingSlopePlayer>,
  direction: -1 | 1,
): number {
  const distance = viewportHalfWidth(player) + ACTOR_SPAWN_MARGIN_METERS;
  return player.worldX + (direction < 0 ? distance : -distance);
}

function actorRecycleDistance(player: Readonly<LivingSlopePlayer>): number {
  return (
    Math.max(viewportTop(player), viewportBottom(player), 45) +
    ACTOR_SPAWN_MARGIN_METERS +
    35
  );
}

function initialActors(seed: string): MutableActor[] {
  void seed;
  const uphillLane = CLASSIC_LIFT.chairs.find(
    (chair) => chair.direction === "uphill",
  )!.laneX;
  const downhillLane = CLASSIC_LIFT.chairs.find(
    (chair) => chair.direction === "downhill",
  )!.laneX;
  const chairs: MutableActor[] = [];
  for (const direction of [-1, 1] as const) {
    for (let index = 0; index < 4; index += 1) {
      const riderCount = [0, 1, 2, 1][(index + (direction > 0 ? 1 : 0)) % 4] as
        0 | 1 | 2;
      chairs.push(
        actor({
          active: true,
          direction,
          id: `chair-${direction < 0 ? "up" : "down"}-${index}`,
          kind: "chairlift",
          riderCount,
          source: "mountain",
          state: riderCount > 0 ? "occupied" : "empty",
          worldX: direction < 0 ? uphillLane : downhillLane,
          worldY: -64 + index * 384 + (direction > 0 ? 192 : 0),
        }),
      );
    }
  }
  return [
    actor({
      active: true,
      direction: 1,
      id: "beginner-0",
      kind: "beginner",
      riderCount: 0,
      source: "mountain",
      state: "cruising",
      velocityY: 4.2,
      worldX: -18,
      worldY: 14,
    }),
    actor({
      active: true,
      direction: -1,
      id: "beginner-1",
      kind: "beginner",
      riderCount: 0,
      source: "mountain",
      state: "wobble",
      velocityY: 4.6,
      worldX: 18,
      worldY: -22,
    }),
    actor({
      active: true,
      direction: -1,
      id: "snowboarder-top",
      kind: "snowboarder",
      riderCount: 0,
      source: "top-entry",
      state: "entering",
      velocityY: 12.5,
      worldX: 14,
      worldY: -38,
    }),
    actor({
      active: false,
      direction: 1,
      id: "snowboarder-chair-drop",
      kind: "snowboarder",
      riderCount: 0,
      source: "chairlift",
      state: "jumping",
      worldX: downhillLane,
      worldY: -64,
    }),
    actor({
      active: true,
      direction: -1,
      id: "dog-0",
      kind: "dog",
      riderCount: 0,
      source: "mountain",
      state: "running-left",
      velocityX: -4.8,
      velocityY: 1.2,
      worldX: 12,
      worldY: 25,
    }),
    actor({
      active: true,
      direction: 1,
      id: "walking-tree-0",
      kind: "walking-tree",
      riderCount: 0,
      source: "mountain",
      state: "idle",
      worldX: 22,
      worldY: 21,
    }),
    ...chairs,
  ];
}

/** Deterministic, engine-independent moving population with a fixed actor pool. */
export class LivingSlopeSimulation {
  private actors: MutableActor[];
  private readonly events: LivingActorEvent[] = [];
  private eventSequence = 0;
  private forcedChairDrop = false;
  private lastEvent: LivingActorEventType | "none" = "none";
  private readonly random: SeededRandom;
  private simulationSeconds = 0;
  private workUnitsLastStep = 0;

  constructor(seed: string, random?: SeededRandom) {
    this.actors = initialActors(seed);
    this.random =
      random ?? createSeededRandom(hashSeed(`living-events:${seed}`));
  }

  getSnapshot(): LivingSlopeSnapshot {
    const actors = this.actors.map((value) =>
      Object.freeze({
        active: value.active,
        direction: value.direction,
        id: value.id,
        kind: value.kind,
        riderCount: value.riderCount,
        source: value.source,
        state: value.state,
        stateSeconds: value.stateSeconds,
        velocityX: value.velocityX,
        velocityY: value.velocityY,
        worldX: value.worldX,
        worldY: value.worldY,
      }),
    );
    return Object.freeze({
      actorCount: actors.length,
      actors: Object.freeze(actors),
      bounded: actors.length <= LIVING_SLOPE_TUNING.maximumActors,
      lastEvent: this.lastEvent,
      simulationSeconds: this.simulationSeconds,
      workUnitsLastStep: this.workUnitsLastStep,
    });
  }

  drainEvents(): LivingActorEvent[] {
    return this.events.splice(0);
  }

  reset(seed = "reset"): void {
    this.actors = initialActors(seed);
    this.events.length = 0;
    this.eventSequence = 0;
    this.forcedChairDrop = false;
    this.lastEvent = "none";
    this.simulationSeconds = 0;
    this.workUnitsLastStep = 0;
  }

  /** Place every self-propelled mountain actor beyond a real viewport edge. */
  placeMovingActorsOffscreen(player: Readonly<LivingSlopePlayer>): void {
    for (const value of this.actors) {
      if (value.kind === "beginner") {
        const second = value.id === "beginner-1";
        value.worldX = player.worldX + (second ? 17 : -17);
        value.worldY = topSpawnY(player) - (second ? 14 : 0);
        value.direction = second ? -1 : 1;
        value.velocityX = 0;
        value.velocityY = 4.2;
        this.changeState(value, second ? "wobble" : "cruising");
      } else if (value.id === "snowboarder-top") {
        value.worldX = player.worldX + 14;
        value.worldY = topSpawnY(player) - 4;
        value.direction = -1;
        value.velocityX = 0;
        value.velocityY = 12.5;
        this.changeState(value, "entering");
      } else if (value.id === "dog-0") {
        value.direction = -1;
        value.worldX = sideSpawnX(player, value.direction);
        value.worldY =
          player.worldY + Math.min(18, viewportBottom(player) * 0.35);
        value.velocityX = -4.8;
        value.velocityY = 1.2;
        this.changeState(value, "running-left");
      } else if (value.id === "walking-tree-0") {
        value.direction = 1;
        value.worldX = sideSpawnX(player, value.direction);
        value.worldY =
          player.worldY + Math.min(20, viewportBottom(player) * 0.4);
        value.velocityX = 0;
        value.velocityY = 0;
        this.changeState(value, "idle");
      }
    }
  }

  repositionActor(id: string, worldX: number, worldY: number): boolean {
    if (![worldX, worldY].every(Number.isFinite)) return false;
    const target = this.actors.find((candidate) => candidate.id === id);
    if (!target) return false;
    target.worldX = worldX;
    target.worldY = worldY;
    if (target.kind === "chairlift")
      target.liftDistance = liftDistanceOnLane(worldY, target.direction);
    target.active = true;
    return true;
  }

  requestChairDrop(): void {
    this.forcedChairDrop = true;
  }

  step(
    player: Readonly<LivingSlopePlayer>,
    deltaSeconds: number,
    ramps: readonly LivingSlopeRamp[] = [],
  ): void {
    if (!Number.isFinite(deltaSeconds) || deltaSeconds <= 0)
      throw new RangeError("deltaSeconds must be finite and positive");
    this.simulationSeconds = player.simulationSeconds;
    this.workUnitsLastStep = 0;
    for (const value of this.actors) {
      this.workUnitsLastStep += 1;
      if (!value.active) continue;
      value.stateSeconds += deltaSeconds;
      if (value.kind === "beginner")
        this.stepBeginner(value, player, deltaSeconds);
      else if (value.kind === "snowboarder")
        this.stepSnowboarder(value, player, ramps, deltaSeconds);
      else if (value.kind === "dog") this.stepDog(value, player, deltaSeconds);
      else if (value.kind === "walking-tree")
        this.stepWalkingTree(value, player, deltaSeconds);
      else this.stepChair(value, deltaSeconds);
    }
  }

  private stepBeginner(
    value: MutableActor,
    player: Readonly<LivingSlopePlayer>,
    deltaSeconds: number,
  ): void {
    if (value.state === "cruising") {
      value.velocityX = value.direction * 1.3;
      value.velocityY = 7;
      if (value.stateSeconds >= 1.1) this.changeState(value, "wobble");
    } else if (value.state === "wobble") {
      value.velocityX =
        value.direction *
        (1.8 + Math.abs(Math.sin(value.stateSeconds * 13)) * 1.8);
      value.velocityY = 6;
      if (value.stateSeconds >= 0.7) {
        const state =
          value.direction > 0 ? "fallen-forward" : "fallen-backward";
        value.direction = value.direction > 0 ? -1 : 1;
        this.changeState(value, state);
        this.emit("beginner-fell", value);
      }
    } else if (
      value.state === "fallen-forward" ||
      value.state === "fallen-backward"
    ) {
      value.velocityX = 0;
      value.velocityY = 0;
      if (value.stateSeconds >= 0.72) this.changeState(value, "recovering");
    } else if (value.state === "recovering") {
      if (value.stateSeconds >= 0.58) {
        this.changeState(value, "cruising");
        this.emit("beginner-recovered", value);
      }
    }
    value.worldX += value.velocityX * deltaSeconds;
    value.worldY += value.velocityY * deltaSeconds;
    if (Math.abs(value.worldY - player.worldY) > actorRecycleDistance(player)) {
      value.worldX = player.worldX + (value.id === "beginner-1" ? 17 : -17);
      value.worldY = topSpawnY(player);
    }
  }

  private stepSnowboarder(
    value: MutableActor,
    player: Readonly<LivingSlopePlayer>,
    ramps: readonly LivingSlopeRamp[],
    deltaSeconds: number,
  ): void {
    const previousX = value.worldX;
    const previousY = value.worldY;
    if (
      value.lastRampId &&
      !ramps.some(
        (ramp) =>
          ramp.id === value.lastRampId &&
          Math.hypot(value.worldX - ramp.worldX, value.worldY - ramp.worldY) <=
            ramp.footprintRadius + 5,
      )
    )
      value.lastRampId = null;
    if (value.state === "entering") {
      value.velocityY = 13.5;
      if (value.stateSeconds >= 0.45) {
        this.changeState(
          value,
          value.direction < 0 ? "carve-left" : "carve-right",
        );
        this.emit("snowboarder-entered", value);
      }
    } else if (value.state === "carve-left" || value.state === "carve-right") {
      value.velocityX = value.state === "carve-left" ? -5.5 : 5.5;
      value.velocityY = 15.5;
      if (value.stateSeconds >= 0.72) {
        value.direction = value.state === "carve-left" ? 1 : -1;
        this.changeState(
          value,
          value.direction < 0 ? "carve-left" : "carve-right",
        );
      }
    } else if (value.state === "jumping") {
      value.velocityX *= 0.98;
      value.velocityY = 16.8;
      if (value.stateSeconds >= 0.62) {
        this.changeState(value, "tumbling-1");
        this.emit("snowboarder-tumbled", value);
      }
    } else if (value.state.startsWith("tumbling-")) {
      value.velocityY = 7.5;
      const phase = Number(value.state.at(-1));
      if (value.stateSeconds >= 0.16) {
        if (phase < 4)
          this.changeState(value, `tumbling-${phase + 1}` as LivingActorState);
        else {
          this.changeState(value, "recovering");
          this.emit("snowboarder-landed", value);
        }
      }
    } else if (value.state === "recovering") {
      value.velocityX = 0;
      value.velocityY = 5;
      if (value.stateSeconds >= 0.5)
        this.changeState(
          value,
          value.direction < 0 ? "carve-left" : "carve-right",
        );
    }
    value.worldX += value.velocityX * deltaSeconds;
    value.worldY += value.velocityY * deltaSeconds;
    if (value.state === "carve-left" || value.state === "carve-right") {
      const ramp = ramps
        .filter((candidate) => candidate.id !== value.lastRampId)
        .map((candidate) => ({
          candidate,
          distance: distanceToSegment(
            candidate.worldX,
            candidate.worldY,
            previousX,
            previousY,
            value.worldX,
            value.worldY,
          ),
        }))
        .filter(
          ({ candidate, distance }) =>
            distance <=
            candidate.footprintRadius +
              LIVING_SLOPE_TUNING.snowboarderFootprintRadiusMeters,
        )
        .sort(
          (left, right) =>
            left.distance - right.distance ||
            left.candidate.id.localeCompare(right.candidate.id),
        )[0]?.candidate;
      if (ramp) {
        value.lastRampId = ramp.id;
        this.changeState(value, "jumping");
        this.emit("snowboarder-jumped", value, ramp.id);
      }
    }
    if (
      value.source === "top-entry" &&
      (Math.abs(value.worldY - player.worldY) > actorRecycleDistance(player) ||
        Math.abs(value.worldX - player.worldX) >
          LIVING_SLOPE_TUNING.actorCullDistanceMeters)
    ) {
      value.worldX = player.worldX + (value.direction < 0 ? 16 : -16);
      value.worldY = topSpawnY(player);
      this.changeState(value, "entering");
    }
    if (value.source === "chairlift" && value.worldY > player.worldY + 100)
      value.active = false;
  }

  private stepDog(
    value: MutableActor,
    player: Readonly<LivingSlopePlayer>,
    deltaSeconds: number,
  ): void {
    if (value.state === "running-left" || value.state === "running-right") {
      value.velocityX = value.state === "running-left" ? -4.8 : 4.8;
      value.velocityY = 1.2;
      const contact = Math.hypot(
        value.worldX - player.worldX,
        value.worldY - player.worldY,
      );
      if (contact <= LIVING_SLOPE_TUNING.dogContactRadiusMeters) {
        value.velocityX = 0;
        value.velocityY = 0;
        this.changeState(value, "knocked");
        this.emit("dog-hit", value);
      } else if (Math.abs(value.worldX - player.worldX) > 28) {
        value.direction = value.worldX > player.worldX ? -1 : 1;
        value.velocityX = value.direction * 4.8;
        this.changeState(
          value,
          value.direction < 0 ? "running-left" : "running-right",
        );
      }
    } else if (value.state === "knocked") {
      if (value.stateSeconds >= 0.65) this.changeState(value, "recovering");
    } else if (value.state === "recovering" && value.stateSeconds >= 0.72) {
      value.direction = value.direction < 0 ? 1 : -1;
      value.velocityX = value.direction * 4.8;
      value.velocityY = 1.2;
      this.changeState(
        value,
        value.direction < 0 ? "running-left" : "running-right",
      );
      this.emit("dog-recovered", value);
    }
    value.worldX += value.velocityX * deltaSeconds;
    value.worldY += value.velocityY * deltaSeconds;
    if (Math.abs(value.worldY - player.worldY) > actorRecycleDistance(player)) {
      value.direction = value.direction < 0 ? 1 : -1;
      value.worldX = sideSpawnX(player, value.direction);
      value.worldY =
        player.worldY + Math.min(18, viewportBottom(player) * 0.35);
      value.velocityX = value.direction * 4.8;
      value.velocityY = 1.2;
      this.changeState(
        value,
        value.direction < 0 ? "running-left" : "running-right",
      );
    }
  }

  private stepWalkingTree(
    value: MutableActor,
    player: Readonly<LivingSlopePlayer>,
    deltaSeconds: number,
  ): void {
    if (value.state === "idle") {
      const distance = Math.hypot(
        value.worldX - player.worldX,
        value.worldY - player.worldY,
      );
      if (
        value.stateSeconds >= 0 &&
        (value.stateSeconds >= 1.5 ||
          distance <= LIVING_SLOPE_TUNING.walkingTreeWakeRadiusMeters)
      ) {
        this.changeState(
          value,
          value.direction < 0 ? "walking-left" : "walking-right",
        );
        this.emit("walking-tree-woke", value);
      }
    } else if (
      value.state === "walking-left" ||
      value.state === "walking-right"
    ) {
      value.velocityX = value.state === "walking-left" ? -2.2 : 2.2;
      value.worldX += value.velocityX * deltaSeconds;
      if (value.stateSeconds >= 2.4) {
        value.direction = value.direction < 0 ? 1 : -1;
        value.velocityX = 0;
        this.changeState(value, "idle");
        value.stateSeconds = -1.2;
        this.emit("walking-tree-moved", value);
      }
    }
    if (Math.abs(value.worldY - player.worldY) > actorRecycleDistance(player)) {
      value.direction = value.direction < 0 ? 1 : -1;
      value.worldX = sideSpawnX(player, value.direction);
      value.worldY = player.worldY + Math.min(20, viewportBottom(player) * 0.4);
      value.velocityX = 0;
      this.changeState(value, "idle");
    }
  }

  private stepChair(value: MutableActor, deltaSeconds: number): void {
    const beforeDistance = value.liftDistance;
    const before = liftRoutePoint(beforeDistance);
    const speed = LIVING_SLOPE_TUNING.chairSpeedMetersPerSecond;
    value.liftDistance = wrapLiftDistance(
      beforeDistance + speed * deltaSeconds,
    );
    const next = liftRoutePoint(value.liftDistance);
    // Apply displacement so debug scenarios can stage chairs beside the player.
    value.worldX += next.x - before.x;
    value.worldY += next.y - before.y;
    value.velocityX = next.tangentX * speed;
    value.velocityY = next.tangentY * speed;
    value.direction = next.tangentY >= 0 ? 1 : -1;
    if (
      value.liftDistance < beforeDistance ||
      (beforeDistance < LIFT_ROUTE.straightLength + LIFT_ROUTE.turnLength &&
        value.liftDistance >= LIFT_ROUTE.straightLength + LIFT_ROUTE.turnLength)
    )
      this.emit("chair-looped", value);

    if (
      value.state === "dropping-skier" ||
      value.state === "dropping-snowboarder"
    ) {
      if (value.stateSeconds >= 0.55)
        this.changeState(value, value.riderCount > 0 ? "occupied" : "empty");
      return;
    }

    if (next.section !== "downhill" || value.riderCount === 0) return;
    if (
      this.forcedChairDrop ||
      this.random.next() < LIVING_SLOPE_TUNING.chairDropChancePerFixedStep
    ) {
      this.forcedChairDrop = false;
      const dropped = this.actors.find(
        (candidate) => candidate.id === "snowboarder-chair-drop",
      )!;
      dropped.active = true;
      dropped.worldX = value.worldX;
      dropped.worldY = value.worldY + 1.5;
      dropped.velocityX = 3.5;
      dropped.velocityY = 12;
      dropped.direction = 1;
      this.changeState(dropped, "jumping");
      this.changeState(
        value,
        value.riderCount === 2 ? "dropping-skier" : "dropping-snowboarder",
      );
      value.riderCount = value.riderCount === 2 ? 1 : 0;
      this.emit("chair-rider-dropped", value);
    }
  }

  private changeState(value: MutableActor, state: LivingActorState): void {
    if (value.state === state) return;
    value.state = state;
    value.stateSeconds = 0;
  }

  private emit(
    type: LivingActorEventType,
    value: MutableActor,
    terrainObjectId: string | null = null,
  ): void {
    this.lastEvent = type;
    this.events.push(
      Object.freeze({
        actorId: value.id,
        atSeconds: this.simulationSeconds,
        id: `living-${this.eventSequence++}`,
        terrainObjectId,
        type,
        worldX: value.worldX,
        worldY: value.worldY,
      }),
    );
  }
}
